import * as React from "react";
import { useState } from "react";
import moment from "moment";
import useProductName from "brand-customization/useProductName";
import useLicenseStatus from "license/useLicenseStatus";
import useLicenses from "license/hooks/useLicenses";
import { licenseProductNames } from "license/helper/licenseProducts";
import Store from "logic/local-storage/Store";
import usePermissions from "hooks/usePermissions";
import LicenseUsageWarningNotification from "license/notifications/LicenseUsageWarningNotification";
import LicenseViolationNotification from "license/notifications/LicenseViolationNotification";
import LicenseExpirationNotification from "license/notifications/LicenseExpirationNotification";
import LicenseExpirationWarningNotification from "license/notifications/LicenseExpirationWarningNotification";
import LicenseTrialNotification from "license/notifications/LicenseTrialNotification";
import LicenseTrafficViolationNotificationContainer from "license/notifications/LicenseTrafficViolationNotificationContainer";
const REMINDER_STORAGE_KEY = "license:expiration-reminder";
const TRIAL_REMINDER_STORAGE_KEY = "license:trial-reminder";
const EXPIRED_LICENSES_SEARCH_PARAMS = {
  page: 1,
  pageSize: 1e3,
  query: "",
  sort: { attributeId: "status", direction: "asc" }
};
const _getExpirationWarningStatus = (licenseStatus) => {
  const newStatus = { show: true, enableReminderButton: false, showTrialWarning: true };
  const { violated, expired, expiresIn } = licenseStatus;
  if (violated || expired) {
    return newStatus;
  }
  const value = Store.get(REMINDER_STORAGE_KEY);
  const expirationDate = moment().add(expiresIn, "seconds");
  const almostExpired = moment().isAfter(expirationDate.subtract(5, "days"));
  const trialMuteValue = Store.get(TRIAL_REMINDER_STORAGE_KEY);
  if (trialMuteValue && trialMuteValue.mutedAt) {
    newStatus.showTrialWarning = moment().isAfter(moment(trialMuteValue.mutedAt).add(1, "days"));
  }
  newStatus.show = almostExpired || !value;
  newStatus.enableReminderButton = !almostExpired;
  return newStatus;
};
const LicenseNotificationContainer = () => {
  const { isPermitted } = usePermissions();
  const productName = useProductName();
  const licenseStatus = useLicenseStatus();
  const canManageLicenses = isPermitted("licenses:create");
  const {
    data: { list: licenses }
  } = useLicenses(EXPIRED_LICENSES_SEARCH_PARAMS, { enabled: licenseStatus.expired && canManageLicenses });
  const [expirationWarningStatus, setExpirationWarningStatus] = useState(
    _getExpirationWarningStatus(licenseStatus)
  );
  const handleExpirationReminder = () => {
    Store.set(REMINDER_STORAGE_KEY, { createdAt: moment().toISOString() });
    setExpirationWarningStatus(_getExpirationWarningStatus(licenseStatus));
  };
  const handleMuteTrialReminder = () => {
    Store.set(TRIAL_REMINDER_STORAGE_KEY, { mutedAt: moment().toISOString() });
    setExpirationWarningStatus(_getExpirationWarningStatus(licenseStatus));
  };
  const children = [];
  const {
    trial,
    trialExpiresIn,
    violated,
    expired,
    expiresIn,
    expiredSince,
    expirationUpcoming,
    trafficViolation,
    usageThresholdWarning,
    usageThreshold
  } = licenseStatus;
  if (trial && expirationWarningStatus.showTrialWarning) {
    const duration = moment.duration(trialExpiresIn, "seconds").humanize();
    children.push(/* @__PURE__ */ React.createElement(LicenseTrialNotification, { expiresIn: duration, key: 1, onMuteClick: handleMuteTrialReminder }));
  }
  if (violated) {
    children.push(/* @__PURE__ */ React.createElement(LicenseViolationNotification, { key: 2 }));
  }
  if (trafficViolation && isPermitted(["notifications:read"])) {
    children.push(/* @__PURE__ */ React.createElement(LicenseTrafficViolationNotificationContainer, { key: 3 }));
  }
  if (usageThresholdWarning && isPermitted(["notifications:read"])) {
    children.push(/* @__PURE__ */ React.createElement(LicenseUsageWarningNotification, { remainingVolume: usageThreshold, key: 4 }));
  }
  if (canManageLicenses) {
    if (expired) {
      const duration = moment.duration(expiredSince, "seconds").humanize();
      const expiredProducts = Array.from(
        new Set(
          licenses.filter((license) => {
            var _a;
            return (_a = license.violations) == null ? void 0 : _a.expired;
          }).flatMap((license) => licenseProductNames(license, productName))
        )
      );
      children.push(/* @__PURE__ */ React.createElement(LicenseExpirationNotification, { expiredSince: duration, products: expiredProducts, key: 5 }));
    } else if (expirationUpcoming && expiresIn > 0 && expirationWarningStatus.show) {
      const duration = moment.duration(expiresIn, "seconds").humanize();
      children.push(
        /* @__PURE__ */ React.createElement(
          LicenseExpirationWarningNotification,
          {
            expiresIn: duration,
            enableReminderButton: expirationWarningStatus.enableReminderButton,
            onReminderClick: handleExpirationReminder,
            key: 6
          }
        )
      );
    }
  }
  return /* @__PURE__ */ React.createElement("div", { className: "license-notification-container" }, children);
};
export default LicenseNotificationContainer;
